import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { BUILDER_CODE_FEE } from '@/config/consts'
import { formatPercentage } from '@/domain/common/format'
import { useBlockExplorerAddressLink } from '@/domain/hooks/useBlockExplorerAddressLink'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { PriceImpactWarning, getImpactLevel } from '@/features/swap/components/PriceImpactWarning'
import { estimateUSDDifference, getPriceImpactPercentage } from '@/features/swap/logic/priceDataFormatters'
import { getTradeType } from '@/features/trade/TradeButton'
import { UseTradeResults, formFormat } from '@/features/trade/logic/useTrade'
import hypurrPaw from '@/ui/assets/hypurr-paw.svg?url'
import { getTokenImage } from '@/ui/assets/tokens'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SlippagePanel, SlippageWarning } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { ArrowRight, ChevronDown, ChevronUp, ExternalLinkIcon, Loader2 } from 'lucide-react'
import NextImage from 'next/image'
import { useState } from 'react'
import { useChainId } from 'wagmi'

const slippageOptions = [Percentage(0.001), Percentage(0.005), Percentage(0.01)]
interface TradeRouteInfoProps extends UseTradeResults {
  slippage: Percentage
  setSlippage?: (slippage: Percentage) => void
}

export function TradeRouteInfo({ slippage, setSlippage, ...trade }: TradeRouteInfoProps) {
  const [showTradeSummary, setShowTradeSummary] = useState(true)
  const chainId = useChainId()
  const nativeAssetInfo = getNativeAssetInfo(chainId)

  // Get router address and route name from price data
  const routerAddress = trade.swapQuoteData && 'result' in trade.swapQuoteData
    ? trade.swapQuoteData.result.router
    : undefined
  const routeName = trade.swapQuoteData && 'result' in trade.swapQuoteData
    ? trade.swapQuoteData.result.liquidityModules[0] ?? 'LiFi'
    : 'LiFi'
  const routerLink = useBlockExplorerAddressLink({ address: routerAddress, chainId })

  const { isDualL1Trade, isL1Trade, isEvmTrade } = getTradeType(
    trade.fromTokenField.selectedAsset,
    trade.toTokenField.selectedAsset,
    nativeAssetInfo.nativeAssetSymbol,
    nativeAssetInfo.wrappedNativeAssetSymbol,
    trade.tradingWithAssetsSymbols,
  )

  const hypeIcon = getTokenImage(TokenSymbol('HYPE'))
  const builderFee = Number(BUILDER_CODE_FEE.replace('%', ''))
  const fee = formFormat(NormalizedUnitNumber(builderFee + Number(trade.hyperliquidFee?.spotTaker || 0)), 4, true)

  const slippagePercentage = `(max. -${slippage.toNumber() * 100}%)`
  const USDDifference = `(${estimateUSDDifference(trade.swapQuoteData)})`

  return (
    <div className="mt-4 flex flex-col gap-3">
      {/* Exchange Rate and Fee Row */}
      <div className="flex flex-col justify-between gap-2">
        <div className="flex flex-row justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-row items-center gap-1 text-muted-foreground text-xs">
            <span className="truncate">1 {trade.fromTokenField.selectedAsset.token.symbol} =</span>{' '}
            {trade.isExchangeRateLoading && (isEvmTrade || isL1Trade) ? (
              <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />
            ) : (
              <>
                <span className="truncate">{trade.exchangeRate}</span>{' '}
                <span className="truncate">{trade.toTokenField.selectedAsset.token.symbol}</span>{' '}
                {isEvmTrade && getImpactLevel(getPriceImpactPercentage(trade.swapQuoteData)) !== 'low' ? (
                  <PriceImpactWarning percentage={getPriceImpactPercentage(trade.swapQuoteData)} />
                ) : (
                  <span className="truncate">
                    {isL1Trade || isDualL1Trade ? slippagePercentage : isEvmTrade ? USDDifference : ''}
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="flex-shrink-0 text-muted-foreground text-xs">
              Fee:{' '}
              {isL1Trade || isDualL1Trade
                ? `${isDualL1Trade ? Number(fee) * 2 : Number(fee)}%`
                : isEvmTrade
                  ? 'Included'
                  : '0'}
            </div>
            {setSlippage !== undefined && (
              <>
                <span className="text-muted-foreground text-xs">
                  Slippage: {formatPercentage(slippage, { minimumFractionDigits: 1 })}{' '}
                  <SlippageWarning slippage={slippage} />
                </span>
                <SlippagePanel
                  slippage={slippage}
                  slippageOptions={slippageOptions}
                  setSlippage={setSlippage}
                  title="Trade Settings"
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Swap Route Panel */}
      <Panel.Wrapper className="p-4">
        <div
          className="flex flex-row items-center justify-between hover:cursor-pointer"
          onClick={() => setShowTradeSummary(!showTradeSummary)}
        >
          <div className="flex min-w-0 flex-1 flex-col justify-between gap-2">
            <Typography variant="p" className="text-muted-foreground text-xs">
              Swap route
            </Typography>
            <div className="flex flex-row items-center gap-1 text-xs">
              <NextImage
                src={isEvmTrade ? hypurrPaw : hypeIcon}
                alt="Route"
                width={16}
                height={16}
                className="flex-shrink-0"
              />
              {isEvmTrade ? (
                <span className="inline-flex items-center gap-1 capitalize">
                  {routeName}
                  <span className="text-muted-foreground normal-case">via</span>
                  <a
                    href={routerLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    LiFi
                    <ExternalLinkIcon className="h-3 w-3 flex-shrink-0" />
                  </a>
                </span>
              ) : (
                <span className="truncate">{isL1Trade || isDualL1Trade ? 'HyperCore' : 'HyperEVM'}</span>
              )}
            </div>
          </div>
          <button className="flex flex-shrink-0 items-center gap-2 text-muted-foreground">
            {showTradeSummary ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {showTradeSummary && (
          <div className="mt-4 flex min-w-0 flex-row items-center gap-4 text-xs">
            {/* From Token */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="flex shrink-0">
                <TokenIcon token={trade.fromToken.token} className="h-6 w-6" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-start">
                <div className="w-full truncate font-semibold text-base text-foreground capitalize">
                  {trade.fromToken.value.toNumber()} {trade.fromToken.token.symbol}
                </div>
                <div className="flex w-full min-w-0 items-center gap-2">
                  <div className="truncate font-normal text-muted-foreground text-sm">
                    {trade.fromToken.token.formatUSD(trade.fromToken.value)}
                  </div>
                </div>
              </div>
            </div>

            {/* Dual L1 Trade Intermediate Step */}
            {isDualL1Trade && (
              <>
                <ArrowRight className="h-6 w-6 flex-shrink-0 text-muted-foreground" />
                <div className="flex items-center gap-2">
                  <NextImage
                    src={getTokenImage(TokenSymbol('USDC'))}
                    alt="USDC"
                    width={24}
                    height={24}
                    className="h-6 w-6"
                  />
                </div>
              </>
            )}

            <ArrowRight className="h-6 w-6 flex-shrink-0 text-muted-foreground" />

            {/* To Token */}
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <div className="flex shrink-0">
                <TokenIcon token={trade.toToken.token} className="h-6 w-6" />
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-start">
                <div className="w-full truncate font-semibold text-base text-foreground capitalize">
                  {trade.toToken.value.toNumber()} {trade.toToken.token.symbol}
                </div>
                <div className="flex w-full min-w-0 items-center gap-2">
                  <div className="truncate font-normal text-muted-foreground text-sm">
                    {trade.toToken.token.formatUSD(trade.toToken.value)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Panel.Wrapper>
    </div>
  )
}
